/**
 * TryQuiPus — Contexto de Identidad de Sesión
 *
 * Almacena quién está operando el sistema en esta sesión.
 * No gestiona contraseñas ni tokens — es identidad local para auditoría.
 * Persiste en localStorage ('quipus_active_user') como metadata de sesión,
 * que es el uso permitido por la Regla de Oro 1 (datos contables solo en IndexedDB).
 *
 * En mount y en cada cambio de usuario llama a db.setCurrentUser(id)
 * para que todas las escrituras en IndexedDB lleven el ID correcto como createdBy.
 */

import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import type { UserRole } from '../services/UserRoles';
import { supabase } from '../services/supabaseClient';

// ═══════════════════════════════════════════════════════════════════
// TIPOS
// ═══════════════════════════════════════════════════════════════════

export interface ActiveUser {
  /** ID estable para auditoría (ej. 'user-delia', UUID, o email). */
  id: string;
  /** Nombre visible en la UI. */
  displayName: string;
  /** Rol que controla qué módulos ve. */
  role: UserRole;
}

interface UserContextType {
  activeUser: ActiveUser;
  setActiveUser: (user: ActiveUser) => void;
  /** true once the Supabase session has resolved and activeUser.id is the real UUID (or 'local-user' if unauthenticated). */
  ready: boolean;
}

// ═══════════════════════════════════════════════════════════════════
// DEFAULTS
// ═══════════════════════════════════════════════════════════════════

const DEFAULT_USER: ActiveUser = {
  id: 'local-user',
  displayName: 'Usuario Local',
  role: 'contador',
};

const STORAGE_KEY = 'quipus_active_user';

// ═══════════════════════════════════════════════════════════════════
// CONTEXTO
// ═══════════════════════════════════════════════════════════════════

const UserContext = createContext<UserContextType | null>(null);

// ═══════════════════════════════════════════════════════════════════
// PROVIDER
// ═══════════════════════════════════════════════════════════════════

export function UserProvider({ children }: { children: ReactNode }) {
  const [activeUser, setActiveUserState] = useState<ActiveUser>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as ActiveUser) : DEFAULT_USER;
    } catch {
      return DEFAULT_USER;
    }
  });
  const [ready, setReady] = useState(false);

  // Sync real Supabase UID into activeUser.id.
  // Only falls back to 'local-user' when there is genuinely no session.
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        const meta = session.user.user_metadata as Record<string, string> | undefined;
        const displayName = meta?.full_name || meta?.name || session.user.email || 'Usuario';
        setActiveUserState(prev => ({ ...prev, id: session.user.id, displayName }));
      }
      setReady(true);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session?.user) return;
      const meta = session.user.user_metadata as Record<string, string> | undefined;
      const displayName = meta?.full_name || meta?.name || session.user.email || 'Usuario';
      setActiveUserState(prev => ({ ...prev, id: session.user.id, displayName }));
    });

    return () => subscription.unsubscribe();
  }, []);

  function setActiveUser(user: ActiveUser): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    setActiveUserState(user);
  }

  return (
    <UserContext.Provider value={{ activeUser, setActiveUser, ready }}>
      {children}
    </UserContext.Provider>
  );
}

// ═══════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════

export function useUserContext(): UserContextType {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error('useUserContext must be used within <UserProvider>');
  return ctx;
}
